import React, { useState } from 'react';
import { ShieldCheck, CheckCircle2, XCircle, ExternalLink, Loader2, Award } from 'lucide-react';
import { api } from '../services/api';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const TYPE_LABELS = {
  completion: 'Completion',
  achievement: 'Achievement',
  participation: 'Participation',
  excellence: 'Excellence',
  mentoring: 'Mentoring',
  consistency: 'Consistency',
};

export default function VerifyCertificate() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);   // { verified, certificate, error }
  const [error, setError] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Please enter a certificate ID.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const data = await api.verifyCertificate(trimmed);
      setResult(data);
    } catch (err) {
      // The backend returns 404 for not found — apiClient throws with the error message.
      setResult({ verified: false, error: err.message || 'Certificate not found.' });
    } finally {
      setLoading(false);
    }
  };

  // ── Inline styles following Ekalavya's premium dark theme ──
  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #0c0612 0%, #150d21 40%, #0f0a1a 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 1rem',
    fontFamily: "'Poppins', sans-serif",
    position: 'relative',
    overflow: 'hidden',
  };

  // Decorative purple glow orbs
  const orbStyle1 = {
    position: 'absolute',
    width: '600px',
    height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(147,51,234,0.08) 0%, transparent 70%)',
    top: '-200px',
    right: '-200px',
    pointerEvents: 'none',
  };
  const orbStyle2 = {
    position: 'absolute',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(126,34,206,0.06) 0%, transparent 70%)',
    bottom: '-150px',
    left: '-150px',
    pointerEvents: 'none',
  };

  const cardStyle = {
    width: '100%',
    maxWidth: '480px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.25rem',
    position: 'relative',
    zIndex: 1,
  };

  const iconContainerStyle = {
    width: '72px',
    height: '72px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, rgba(147,51,234,0.12) 0%, rgba(126,34,206,0.08) 100%)',
    border: '1.5px solid rgba(147,51,234,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    animation: 'floatBadge 3s ease-in-out infinite',
  };

  const iconCheckStyle = {
    position: 'absolute',
    bottom: '-3px',
    right: '-3px',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #9333ea, #7e22ce)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #150d21',
  };

  const headingStyle = {
    fontSize: '1.45rem',
    fontWeight: 600,
    color: '#ffffff',
    textAlign: 'center',
    letterSpacing: '-0.01em',
    margin: 0,
    lineHeight: 1.3,
  };

  const subtextStyle = {
    fontSize: '0.82rem',
    color: 'rgba(255,255,255,0.45)',
    textAlign: 'center',
    marginTop: '-0.35rem',
    lineHeight: 1.5,
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: '10px',
    border: '1.5px solid rgba(147,51,234,0.2)',
    background: 'rgba(255,255,255,0.04)',
    color: '#ffffff',
    fontSize: '0.88rem',
    fontFamily: "'Poppins', sans-serif",
    outline: 'none',
    transition: 'all 0.3s ease',
    letterSpacing: '0.01em',
  };

  const btnStyle = {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '10px',
    border: 'none',
    background: loading
      ? 'rgba(147,51,234,0.3)'
      : 'linear-gradient(135deg, rgba(147,51,234,0.2) 0%, rgba(126,34,206,0.15) 100%)',
    color: '#c084fc',
    fontSize: '0.9rem',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    letterSpacing: '0.01em',
    border: '1px solid rgba(147,51,234,0.25)',
  };

  const successBoxStyle = {
    width: '100%',
    padding: '1.1rem 1.25rem',
    borderRadius: '12px',
    background: 'rgba(16,185,129,0.06)',
    border: '1.5px solid rgba(16,185,129,0.25)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    animation: 'slideUp 0.4s cubic-bezier(0.16,1,0.3,1)',
  };

  const failBoxStyle = {
    width: '100%',
    padding: '1.1rem 1.25rem',
    borderRadius: '12px',
    background: 'rgba(239,68,68,0.06)',
    border: '1.5px solid rgba(239,68,68,0.25)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    animation: 'slideUp 0.4s cubic-bezier(0.16,1,0.3,1)',
  };

  const viewBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.5rem 0.9rem',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, rgba(147,51,234,0.15), rgba(126,34,206,0.1))',
    border: '1px solid rgba(147,51,234,0.3)',
    color: '#c084fc',
    fontSize: '0.82rem',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    marginTop: '0.2rem',
    width: 'fit-content',
  };

  return (
    <div style={pageStyle}>
      {/* CSS animations */}
      <style>{`
        @keyframes floatBadge {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .verify-input:focus {
          border-color: rgba(147,51,234,0.5) !important;
          box-shadow: 0 0 0 3px rgba(147,51,234,0.1), 0 0 20px rgba(147,51,234,0.08) !important;
        }
        .verify-input::placeholder {
          color: rgba(255,255,255,0.25);
        }
        .verify-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, rgba(147,51,234,0.35) 0%, rgba(126,34,206,0.25) 100%) !important;
          border-color: rgba(147,51,234,0.4) !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 20px rgba(147,51,234,0.15);
        }
        .view-cert-btn:hover {
          background: linear-gradient(135deg, rgba(147,51,234,0.25), rgba(126,34,206,0.2)) !important;
          border-color: rgba(147,51,234,0.45) !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(147,51,234,0.12);
        }
        .back-link {
          color: rgba(255,255,255,0.35);
          font-size: 0.85rem;
          text-decoration: none;
          transition: color 0.3s ease;
          margin-top: 1.5rem;
        }
        .back-link:hover {
          color: #c084fc;
        }
      `}</style>

      {/* Background orbs */}
      <div style={orbStyle1} />
      <div style={orbStyle2} />

      <div style={cardStyle}>
        {/* Badge Icon */}
        <div style={iconContainerStyle}>
          <ShieldCheck size={32} color="#c084fc" strokeWidth={1.8} />
          <div style={iconCheckStyle}>
            <Award size={11} color="#ffffff" strokeWidth={2.5} />
          </div>
        </div>

        {/* Heading */}
        <h1 style={headingStyle}>Verify Your Certificate</h1>
        <p style={subtextStyle}>
          The certificate ID can be found at the bottom-right corner of your Ekalavya certificate
        </p>

        {/* Form */}
        <form onSubmit={handleVerify} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <input
            className="verify-input"
            type="text"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(''); }}
            placeholder="Enter your certificate ID (e.g. EKL-A3X9K7)"
            style={inputStyle}
            autoFocus
          />

          <button
            type="submit"
            className="verify-btn"
            style={btnStyle}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Verifying…
              </>
            ) : (
              'Validate'
            )}
          </button>
        </form>

        {/* Inline error (empty code etc.) */}
        {error && (
          <div style={{
            ...failBoxStyle,
            padding: '0.85rem 1rem',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '0.5rem',
          }}>
            <XCircle size={18} color="#ef4444" />
            <span style={{ color: '#fca5a5', fontSize: '0.85rem' }}>{error}</span>
          </div>
        )}

        {/* ── Success result ── */}
        {result && result.verified && (
          <div style={successBoxStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle2 size={18} color="#10b981" />
                <span style={{ color: '#34d399', fontWeight: 600, fontSize: '0.95rem' }}>
                  Certificate Verified
                </span>
              </div>
              {result.certificate.type && (
                <span style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '12px',
                  background: 'rgba(147, 51, 234, 0.15)',
                  color: '#c084fc',
                  border: '1px solid rgba(147, 51, 234, 0.3)',
                }}>
                  {TYPE_LABELS[result.certificate.type] || result.certificate.type}
                </span>
              )}
            </div>

            <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '0.75rem', marginTop: '0.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {/* Title & Recipient */}
              <div>
                <h3 style={{ color: '#ffffff', fontSize: '1rem', fontWeight: 600, margin: 0, lineHeight: 1.35 }}>
                  {result.certificate.title}
                </h3>
                <p style={{ color: '#c084fc', fontSize: '0.88rem', fontWeight: 500, margin: '0.25rem 0 0 0' }}>
                  Issued to: {result.certificate.recipientName || result.certificate.recipient?.name || 'Verified Scholar'}
                </p>
              </div>

              {/* Description */}
              {result.certificate.description && (
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', margin: 0, lineHeight: 1.5 }}>
                  {result.certificate.description}
                </p>
              )}

              {/* Key Details Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: '0.5rem',
                marginTop: '0.25rem',
                background: 'rgba(0, 0, 0, 0.2)',
                padding: '0.65rem 0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.05)',
              }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Certificate ID</div>
                  <div style={{ color: '#ffffff', fontSize: '0.82rem', fontWeight: 500, fontFamily: 'monospace' }}>{result.certificate.certificateCode}</div>
                </div>
                {result.certificate.issuedAt && (
                  <div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Issued Date</div>
                    <div style={{ color: '#ffffff', fontSize: '0.82rem', fontWeight: 500 }}>{formatDate(result.certificate.issuedAt)}</div>
                  </div>
                )}
                {result.certificate.metadata?.programHead && (
                  <div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Program Head</div>
                    <div style={{ color: '#ffffff', fontSize: '0.82rem', fontWeight: 500 }}>{result.certificate.metadata.programHead}</div>
                  </div>
                )}
                {result.certificate.recipient?.institution && (
                  <div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Institution</div>
                    <div style={{ color: '#ffffff', fontSize: '0.82rem', fontWeight: 500 }}>{result.certificate.recipient.institution}</div>
                  </div>
                )}
              </div>

              {/* Certificate Action Button */}
              {result.certificate.certificateUrl && (
                <a
                  href={result.certificate.certificateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-cert-btn"
                  style={viewBtnStyle}
                >
                  <ExternalLink size={14} />
                  View Certificate PDF / Image
                </a>
              )}
            </div>
          </div>
        )}

        {/* ── Failed result ── */}
        {result && !result.verified && (
          <div style={failBoxStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <XCircle size={18} color="#ef4444" />
              <span style={{ color: '#fca5a5', fontWeight: 600, fontSize: '0.95rem' }}>
                {result.certificate ? 'Certificate Revoked' : 'Certificate Not Found'}
              </span>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', margin: 0, lineHeight: 1.5 }}>
              {result.error || 'No certificate found with this code. Please check and try again.'}
            </p>
          </div>
        )}

        {/* Back to home link */}
        <a href="/" className="back-link">
          ← Back to Ekalavya
        </a>
      </div>
    </div>
  );
}
